<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Wind Druid Build Guide</h1>
    
    <p>The Wind Druid is a powerful elemental caster build that primarily utilizes <strong>Hurricane</strong> for cold damage and <strong>Tornado</strong> for physical damage. With high survivability from <strong>Cyclone Armor</strong> and massive crowd control, this build excels at clearing Hell difficulty. By leveraging strong elemental damage and defensive capabilities, the Wind Druid is a formidable choice for PvE.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-wind-dru.png">
    </div>

    <div id="slot-content_mpu" class="content-ad-mpu"/>
    
    <h2>Recommended Gear</h2>
    <Equipments
      class="right"
      :left-weapon="weapon"
      :right-weapon="shield"
      :helm="helm"
      :armor="armor"
      :gloves="gloves"
      :belt="belt"
    />
    <h3>Weapons</h3>
    <ul>
        <li><strong>Doom Runeword</strong> (Holy Freeze Aura, reduces enemy cold resistance)</li>
        <li><strong>Heart of the Oak (Hoto)</strong> (+3 Skills, high resistances, FCR)</li>
        <li><strong>Spirit Runeword</strong> (FCR, +2 Skills, mana)</li>
    </ul>
    
    <h3>Shield</h3>
    <ul>
        <li><strong>Spirit Monarch</strong> (+2 Skills, high FCR, mana boost)</li>
    </ul>
    
    <h3>Helmet</h3>
    <ul>
        <li><strong>Nightwing's Veil</strong> (Boosts cold damage, enhances Hurricane effectiveness)</li>
        <li><strong>Ravenlore</strong> (Elemental damage boost, fire resistances)</li>
    </ul>
    
    <h3>Armor</h3>
    <ul>
        <li><strong>Enigma</strong> (Teleport for mobility, strength boost)</li>
    </ul>
    
    <h3>Belt</h3>
    <ul>
        <li><strong>Arachnid Mesh</strong> (+1 Skills, Faster Cast Rate)</li>
    </ul>
    
    <h3>Gloves</h3>
    <ul>
        <li><strong>Trang-Oul’s Claws</strong> (+20% Faster Cast Rate, Cold Resist)</li>
        <li><strong>Magefist</strong> (+20% Faster Cast Rate)</li>
    </ul>
    
    <h3>Rings</h3>
    <ul>
        <li><strong>Stone of Jordan (SOJ)</strong> (+1 Skills, mana boost)</li>
        <li><strong>10% FCR Ring</strong> (If aiming for 99% FCR breakpoint)</li>
    </ul>
    
    <h3>Amulet</h3>
    <ul>
        <li><strong>Mara’s Kaleidoscope</strong> (+2 Skills, high resistances)</li>
    </ul>

    <h2>Core Skill Allocation</h2>
    <div class="m-3 skills-tab right">
      <b-tabs small content-class="m-2" class="tab-text">
        <b-tab title="Elemental" active>
          <img class="w-100" src="@/assets/builds/skills-wind-dru-1.png">
        </b-tab>
        <b-tab title="Shape Shifting">
          <img class="w-100" src="@/assets/builds/skills-wind-dru-2.png">
        </b-tab>
        <b-tab title="Summoning">
          <img class="w-100" src="@/assets/builds/skills-wind-dru-3.png">
        </b-tab>
      </b-tabs>
    </div>
    <ul>
        <li><strong>Tornado</strong> - 20 points (Primary physical damage skill)</li>
        <li><strong>Hurricane</strong> - 20 points (Cold damage for AoE control)</li>
        <li><strong>Cyclone Armor</strong> - 20 points (Absorbs elemental damage)</li>
        <li><strong>Twister</strong> - 20 points (Synergy for Tornado)</li>
        <li><strong>Oak Sage</strong> - 20 points (Increases life pool for survivability)</li>
        <li><strong>Raven</strong> - 1 point (Pre-requisite, useful for blinding enemies)</li>
        <li><strong>Summon Spirit Wolf</strong> - 1 point (Pre-requisite)</li>
        <li><strong>Summon Dire Wolf</strong> - 1 point (Pre-requisite)</li>
        <li><strong>Summon Grizzly</strong> - 1 point (Can act as a tank)</li>
    </ul>
    
    <h2>Attribute Allocation</h2>
    <ul>
        <li><strong>Strength</strong>: Just enough for gear (typically 156 for Spirit Monarch)</li>
        <li><strong>Dexterity</strong>: Just enough for gear</li>
        <li><strong>Vitality</strong>: All remaining points</li>
        <li><strong>Energy</strong>: No points (Mana sustain from gear)</li>
    </ul>
    
    <h2>Mercenary Choice (Act 2 Prayer Aura)</h2>
    <ul>
        <li><strong>Weapon</strong>: Infinity (Reduces enemy resistances, boosts damage)</li>
        <li><strong>Helmet</strong>: Cure (Additional healing for survivability)</li>
        <li><strong>Armor</strong>: Chains of Honor (Boosts resistances and survivability)</li>
    </ul>
    
    <h2>Combat Strategy</h2>
    <ol>
        <li><strong>Keep Hurricane active</strong>: Provides constant cold damage and slows enemies.</li>
        <li><strong>Cast Cyclone Armor</strong>: Absorbs elemental damage for survivability.</li>
        <li><strong>Use Tornado as your primary attack</strong>: Deals high physical damage.</li>
        <li><strong>Summon Oak Sage</strong>: Boosts life pool for you and allies.</li>
        <li><strong>Summon Grizzly if needed</strong>: Acts as a tank to absorb damage.</li>
        <li><strong>Utilize Teleport (Enigma) for repositioning</strong>: Ensures safety and mobility.</li>
    </ol>
    
    <p>The Wind Druid excels in survivability and area-of-effect damage, making it a strong PvE choice. With the right gear and skill setup, it can efficiently clear Hell difficulty and withstand heavy elemental damage.</p>


  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'WindDru',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-crystal-sword',
        'width': 80,
        'title': 'Doom Runeword',
        'runes': ' Hel + Ohm + Um + Lo + Cham',
        'detail': [
          '5% Chance To Cast Level 18 Volcano On Striking',
          'Level 12 Holy Freeze Aura When Equipped',
          '+2 To All Skills',
          '+45% Increased Attack Speed',
          "+330-370% Enhanced Damage",
          '-(40-60)% To Enemy Cold Resistance',
          '20% Deadly Strike',
          '25% Chance of Open Wounds',
          'Prevent Monster Heal',
          'Freezes Target +3',
          'Requirements -20%'
        ],
      }
    },

    shield () {
      return {
        'img': 'eq-monarch',
        'width': 80,
        'title': 'Spirit Runeword',
        'runes': 'Tal + Thul + Ort + Amn',
        'detail': [
          '+2 To All Skills', 
          '+25-35% Faster Cast Rate',
          '+55% Faster Hit Recovery',
          '+250 Defense Vs. Missile', 
          '+22 To Vitality',
          '+89-112 To Mana',
          'Cold Resist +35%',
          'Lightning Resist +35%',
          'Poison Resist +35%',
          '+3-8 Magic Absorb',
          'Attacker Takes Damage of 14',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-nightwings-veil',
        'width': 75,
        'title': "Nightwing's Veil",
        'detail': [
          'Defense: 304-352', 
          '+90-120% Enhanced Defense',
          '+2 To All Skills',
          '+8-15% To Cold Skill Damage', 
          '+10-20 To Dexterity',
          '+5-9 Cold Absorb',
          'Half Freeze Duration',
          'Requirements -50%',
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Enigma Runeword',
        'runes': 'Jah + Ith + Ber',
        'detail': [
          '+2 To All Skills', 
          '+45% Faster Run/Walk',
          '+1 To Teleport',
          '+750-775 Defense', 
          '0-74 To Strength (Based On Character Level)',
          'Increase Maximum Life 5%',
          'Damage Reduced By 8%',
          '+14 Life After Each Kill',
          '15% Damage Taken Goes To Mana',
          '+1-99% Better Chance of Getting Magic Items (Based On Character Level)'
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-trang-oulsclaws',
        'width': 70,
        'title': "Trang-Oul's Claws",
        'detail': [
          'Defense: 67-74', 
          '+20% Faster Cast Rate',
          'Cold Resist +30%',
          '+30 Defense', 
          '+25% Poison Skill Damage',
          '+2 To Curses (Necromancer Only)',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-arachnid-mesh',
        'width': 75,
        'title': 'Arachnid Mesh',
        'detail': [
          'Defense: 119-138', 
          '+90-120% Enhanced Defense',
          'Slows Target By 10%',
          '+1 To All Skills', 
          '+20% Faster Cast Rate',
          'Increases Maximum Mana 5%',
          'Level 3 Venom (11 Charges)',
        ],
      }
    },
  }
}
</script>
